// Unified data fetch. Routes to GAS backend when USE_GAS_AUTH is true,
// falls back to the legacy gviz Google Sheets endpoint otherwise.

import { USE_GAS_AUTH, GAS_URL } from './config.js';
import { SHEET_ID } from './constants.js';
import { getCachedSession, verifyWithBackend } from './auth-gsi.js';

/**
 * Fetch the KPI/GMV/PL dataset for the current session.
 * Returns { gmv, kpi, pl_actual } — same shape as the legacy gviz path expects.
 */
export async function fetchData() {
  if (USE_GAS_AUTH) return fetchFromGas();
  return fetchFromGviz();
}

async function fetchFromGas() {
  const { token } = getCachedSession();
  if (!token) throw new Error('not_authenticated');
  const r = await verifyWithBackend(token, 'fetchData');
  if (!r.ok) throw new Error(r.error || 'fetch_failed');
  return r.data;
}

// Legacy path — preserved so the existing dashboard keeps working until the
// admin flips USE_GAS_AUTH on. Reads three tabs via Google's visualization
// API. Required the sheet to be link-shared "Anyone with the link".
async function fetchFromGviz() {
  if (!SHEET_ID) return { gmv: [], kpi: [], pl_actual: [] };
  const url = (tab) =>
    `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=${tab}`;
  const parse = (t) => {
    const m = t.match(/setResponse\(([\s\S]*)\)/);
    if (!m) return [];
    const table = JSON.parse(m[1]).table;
    const rows = [];
    rows.push((table.cols || []).map((c) => c.label || c.id));
    (table.rows || []).forEach((r) => {
      rows.push((r.c || []).map((c) => c?.v ?? null));
    });
    return rows;
  };
  const [g, k, p] = await Promise.all([
    fetch(url('gmv')).then((r) => r.text()),
    fetch(url('kpi')).then((r) => r.text()),
    fetch(url('pl_actual')).then((r) => r.text()),
  ]);
  return { gmv: parse(g), kpi: parse(k), pl_actual: parse(p) };
}
